import { EditOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Divider, Empty, Flex, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { dedupeNotificationsByContent } from "../../../common/functions.ts";
import { canEditStandbyPeriod } from "../../../common/utils/canEditStandbyPeriod.ts";
import { formatTotalTime } from "../../../common/utils/formatTotalTime.ts";
import { matchStandbyPeriodsToResumeEntries } from "../../../common/utils/standbyPeriodMatching.ts";
import { getUserDisplayName } from "../../../common/utils/userDisplayName.ts";
import { ticketActions } from "../../../features/ticket/ticketSlice.ts";
import type {
  Notification as DelayNotification,
  TicketStandbyPeriod,
  WorkLog,
} from "../../generated-axios-client/index.ts";
import type { RootState } from "../../store";
import type { FieldDiff, LogContext } from "../worklog/workLogFormatter.ts";
import { buildFieldDiffs, formatActor } from "../worklog/workLogFormatter.ts";
import type { StandbyPeriodEditPayload } from "./EditStandbyPeriodModal.tsx";
import { EditStandbyPeriodModal } from "./EditStandbyPeriodModal.tsx";

type Props = {
  intl: IntlShape;
  ticketId: string;
  logContext: LogContext;
};

type HistoryEntry =
  | {
      kind: "worklog";
      id: string;
      at?: string;
      diffs: FieldDiff[];
      changedByName?: string;
      standbyPeriod?: TicketStandbyPeriod;
    }
  | {
      kind: "standby" | "rejection";
      id: string;
      at?: string;
      message: string;
      senderName?: string;
    };

type DateGroup = {
  date: string | null;
  entries: HistoryEntry[];
};

// created_at is declared optional on the generated types because the serializer marks it
// required=False — an input-side rule only, since the column is auto-populated and always
// returned. The guard still matters: dayjs(undefined) resolves to "now" and reports
// isValid() === true, so an entry with no timestamp would silently claim the current date
// instead of being recognised as undated. Presence has to be checked before parsing.
function parseTimestamp(timestamp: string | undefined) {
  if (!timestamp) return null;
  const parsed = dayjs(timestamp);
  return parsed.isValid() ? parsed : null;
}

function formatTimestamp(timestamp: string | undefined, template: string): string | null {
  return parseTimestamp(timestamp)?.format(template) ?? null;
}

// Newest first, with undated entries pushed to the end so they never displace dated ones.
function compareNewestFirst(first: HistoryEntry, second: HistoryEntry): number {
  const firstTimestamp = parseTimestamp(first.at)?.valueOf() ?? null;
  const secondTimestamp = parseTimestamp(second.at)?.valueOf() ?? null;
  if (firstTimestamp === null && secondTimestamp === null) return 0;
  if (firstTimestamp === null) return 1;
  if (secondTimestamp === null) return -1;
  return secondTimestamp - firstTimestamp;
}

function buildNotificationEntries(
  notifications: DelayNotification[],
  kind: "standby" | "rejection"
): HistoryEntry[] {
  // Both endpoints store one row per recipient, so a single report repeats —
  // collapse to one entry per (sender, message, minute) before merging.
  return dedupeNotificationsByContent(notifications).map((notification, index) => ({
    kind,
    id: notification.id ?? `${kind}-${index}`,
    at: notification.created_at,
    message: notification.message ?? "",
    senderName: notification.sender ? getUserDisplayName(notification.sender) : undefined,
  }));
}

function buildHistoryEntries(
  worklogEntries: WorkLog[],
  standbyNotifications: DelayNotification[],
  rejectionNotifications: DelayNotification[],
  standbyPeriods: TicketStandbyPeriod[],
  ctx: LogContext
): HistoryEntry[] {
  const worklogHistoryEntries: HistoryEntry[] = [];

  // Resolved here rather than in the renderer because the raw old_fields/new_fields a
  // resume row is recognised by do not survive onto the formatted HistoryEntry, and
  // because the match is one-to-one across the whole list, not per row.
  const standbyPeriodsByEntryId = matchStandbyPeriodsToResumeEntries(
    worklogEntries,
    standbyPeriods
  );

  worklogEntries.forEach((entry, index) => {
    const diffs = buildFieldDiffs(entry, ctx);
    if (diffs.length === 0) return;

    // Comment entries already bake the actor into their diff label ("Comment added by X") —
    // showing changedByName again above would duplicate it.
    const isCommentEntry = diffs.some((diff) => diff.summary !== undefined);
    const changedByName = isCommentEntry
      ? undefined
      : (formatActor(entry.changed_by, ctx.userMap) ?? undefined);

    worklogHistoryEntries.push({
      kind: "worklog",
      id: entry.id ?? `worklog-${index}`,
      at: entry.created_at,
      diffs,
      changedByName,
      standbyPeriod:
        entry.id !== undefined ? standbyPeriodsByEntryId[entry.id] : undefined,
    });
  });

  return [
    ...worklogHistoryEntries,
    ...buildNotificationEntries(standbyNotifications, "standby"),
    ...buildNotificationEntries(rejectionNotifications, "rejection"),
  ].sort(compareNewestFirst);
}

// Relies on the entries already being sorted newest-first: the Map preserves insertion
// order, so the date groups and the rows inside each of them stay newest-first too.
function groupByDate(historyEntries: HistoryEntry[]): DateGroup[] {
  const groups: Map<string | null, HistoryEntry[]> = new Map();

  for (const entry of historyEntries) {
    const date = formatTimestamp(entry.at, "DD/MM/YYYY");
    if (!groups.has(date)) groups.set(date, []);
    groups.get(date)!.push(entry);
  }

  return Array.from(groups.entries()).map(([date, entries]) => ({ date, entries }));
}

function DiffLine({ diff, intl }: { diff: FieldDiff; intl: IntlShape }) {
  if (diff.summary !== undefined) {
    return (
      <Typography.Text style={{ fontSize: 13 }}>
        <Typography.Text strong style={{ fontSize: 13 }}>
          {diff.label}
        </Typography.Text>
        {diff.summary ? `: ${diff.summary}` : ""}
      </Typography.Text>
    );
  }

  const hasBefore = diff.before !== "";
  const hasAfter = diff.after !== "";

  let value: React.ReactNode;
  if (hasBefore && hasAfter) {
    value = (
      <>
        <Typography.Text type="secondary">{diff.before}</Typography.Text>
        {" → "}
        <Typography.Text>{diff.after}</Typography.Text>
      </>
    );
  } else if (!hasBefore && hasAfter) {
    value = (
      <>
        <Typography.Text type="secondary">
          {intl.formatMessage({ id: "work_log_added" })}:{" "}
        </Typography.Text>
        <Typography.Text>{diff.after}</Typography.Text>
      </>
    );
  } else {
    value = (
      <>
        <Typography.Text type="secondary">
          {intl.formatMessage({ id: "work_log_removed" })}:{" "}
        </Typography.Text>
        <Typography.Text delete>{diff.before}</Typography.Text>
      </>
    );
  }

  return (
    <Typography.Text style={{ fontSize: 13 }}>
      <Typography.Text strong style={{ fontSize: 13 }}>
        {diff.label}
      </Typography.Text>
      {": "}
      {value}
    </Typography.Text>
  );
}

// Standby and rejection rows no longer sit under their own section headings, so the tag is
// what identifies them once they are interleaved with the field diffs.
function HistoryEntryRow({
  entry,
  intl,
  canEditStandby,
  onEditStandby,
}: {
  entry: HistoryEntry;
  intl: IntlShape;
  canEditStandby: boolean;
  onEditStandby: (period: TicketStandbyPeriod) => void;
}) {
  // Read into a const so the click handler closes over a narrowed value rather than
  // re-asserting the optional property inside the callback.
  const standbyPeriod = entry.kind === "worklog" ? entry.standbyPeriod : undefined;

  return (
    <Flex gap={16} align="flex-start">
      <Typography.Text
        type="secondary"
        style={{ fontSize: 12, minWidth: 40, paddingTop: 2, flexShrink: 0 }}
      >
        {formatTimestamp(entry.at, "HH:mm") ?? "—"}
      </Typography.Text>
      {entry.kind === "worklog" ? (
        <Flex vertical gap={4}>
          {entry.changedByName && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {intl.formatMessage(
                { id: "work_log_changed_by" },
                { actor: entry.changedByName }
              )}
            </Typography.Text>
          )}
          {entry.diffs.map((diff, i) => (
            /* biome-ignore lint/suspicious/noArrayIndexKey: this is the inner diff list of
               one history entry (the entries themselves are keyed by id). FieldDiff carries
               no id, and a content key of label/before/after can genuinely collide, since
               buildItemDiffs labels per task and two tasks sharing a blade can record the
               same change. The list is rebuilt on every render, never reordered or spliced,
               and DiffLine holds no state, so the index is a stable identity here. */
            <DiffLine key={i} diff={diff} intl={intl} />
          ))}
          {standbyPeriod !== undefined && (
            <Flex gap={4} align="baseline">
              <Typography.Text style={{ fontSize: 13 }}>
                <Typography.Text strong style={{ fontSize: 13 }}>
                  {intl.formatMessage({ id: "work_log_standby_duration_label" })}
                </Typography.Text>
                {": "}
                <Typography.Text>
                  {formatTotalTime(standbyPeriod.duration_seconds)}
                </Typography.Text>
              </Typography.Text>
              {canEditStandby && (
                <Tooltip title={intl.formatMessage({ id: "standby_period_edit_action" })}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    aria-label={intl.formatMessage({ id: "standby_period_edit_action" })}
                    onClick={() => onEditStandby(standbyPeriod)}
                  />
                </Tooltip>
              )}
            </Flex>
          )}
        </Flex>
      ) : (
        <Flex vertical gap={2}>
          <Flex gap={8} align="baseline" wrap>
            <Tag
              color={entry.kind === "standby" ? "warning" : "error"}
              style={{ marginInlineEnd: 0 }}
            >
              {intl.formatMessage({
                id:
                  entry.kind === "standby"
                    ? "work_log_standby_entry_label"
                    : "work_log_rejection_entry_label",
              })}
            </Tag>
            <Typography.Text style={{ fontSize: 13 }}>{entry.message}</Typography.Text>
          </Flex>
          {entry.senderName && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {intl.formatMessage(
                { id: "work_log_changed_by" },
                { actor: entry.senderName }
              )}
            </Typography.Text>
          )}
        </Flex>
      )}
    </Flex>
  );
}

export const WorkLogInfo = ({ intl, ticketId, logContext }: Props) => {
  const dispatch = useDispatch();
  const entries = useSelector(
    (state: RootState) => state.ticket.worklogEntries ?? []
  ) as WorkLog[];
  const delays = useSelector(
    (state: RootState) => state.ticket.ticketDelays ?? []
  ) as DelayNotification[];
  const rejections = useSelector(
    (state: RootState) => state.ticket.ticketRejections ?? []
  ) as DelayNotification[];
  const standbyPeriods = useSelector(
    (state: RootState) => state.ticket.standbyPeriods ?? []
  ) as TicketStandbyPeriod[];
  const currentUser = useSelector((state: RootState) => state.userstate.user);
  const editStatus = useSelector(
    (state: RootState) => state.ticket.editStandbyPeriodStatus
  );

  const [editingPeriod, setEditingPeriod] = useState<TicketStandbyPeriod | null>(null);
  const canEditStandby = canEditStandbyPeriod(currentUser?.groups);

  useEffect(() => {
    if (editStatus === "succeeded") {
      setEditingPeriod(null);
    }
  }, [editStatus]);

  const openStandbyEditor = (period: TicketStandbyPeriod) => {
    // A previous edit leaves the status at "succeeded", which would close this one the
    // moment it opened.
    dispatch(ticketActions.setEditStandbyPeriodStatus("idle"));
    setEditingPeriod(period);
  };

  const submitStandbyEdit = (payload: StandbyPeriodEditPayload) => {
    if (editingPeriod?.id === undefined) return;
    dispatch(
      ticketActions.editStandbyPeriod({
        periodId: editingPeriod.id,
        ticketId,
        ...payload,
      })
    );
  };

  const groups = useMemo(
    () =>
      groupByDate(
        buildHistoryEntries(entries, delays, rejections, standbyPeriods, logContext)
      ),
    [entries, delays, rejections, standbyPeriods, logContext]
  );

  if (groups.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={intl.formatMessage({ id: "work_log_empty" })}
      />
    );
  }

  return (
    <>
      <Flex vertical gap={0}>
        {groups.map((group) => (
          <Flex vertical key={group.date ?? "undated"}>
            <Divider plain style={{ marginTop: 8 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {group.date ?? intl.formatMessage({ id: "work_log_unknown_date" })}
              </Typography.Text>
            </Divider>
            <Flex vertical gap={12} style={{ paddingLeft: 8 }}>
              {group.entries.map((entry) => (
                <HistoryEntryRow
                  key={entry.id}
                  entry={entry}
                  intl={intl}
                  canEditStandby={canEditStandby}
                  onEditStandby={openStandbyEditor}
                />
              ))}
            </Flex>
          </Flex>
        ))}
      </Flex>
      {editingPeriod !== null && (
        <EditStandbyPeriodModal
          open
          period={editingPeriod}
          loading={editStatus === "loading"}
          intl={intl}
          onSubmit={submitStandbyEdit}
          onCancel={() => setEditingPeriod(null)}
        />
      )}
    </>
  );
};
